import type { ReactNode } from 'react';
import { ChevronRight, Eye } from 'lucide-react';
import { formatDate, initials } from '@/shared/lib/format';
import { isMasterEmail } from '@/shared/lib/navigation';
import { cn } from '@/shared/lib/cn';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tooltip } from '@/shared/ui/tooltip';
import { EmptyState } from '@/shared/components/empty-state';
import { Users } from 'lucide-react';
import { CreditsBar } from '@/features/ai-credits/ui/credits-bar';
import type { AiUserRow } from '@/features/ai-credits/model/types';
import { linkedStores, type StoreCatalogItem } from '../model/linked-stores';
import { panelRoleLabel } from '../model/roles';

export interface UsersTableSelection {
  selectedIds: Set<string>;
  toggle: (id: string) => void;
  allOnPageSelected: boolean;
  someOnPageSelected: boolean;
  toggleAllOnPage: () => void;
}

interface UsersTableProps {
  rows: AiUserRow[];
  loading?: boolean;
  fetching?: boolean;
  /** Coluna Passbook — na lista global, onde cada linha pode ser de um passbook. */
  showPassbook?: boolean;
  storeCatalog?: StoreCatalogItem[];
  selection?: UsersTableSelection;
  onOpen: (user: AiUserRow) => void;
  /** Abre o painel como este usuário (só o master). */
  onViewAs?: (user: AiUserRow) => void;
  onToggleActive?: (user: AiUserRow, next: boolean) => void;
  busyIds?: Set<string>;
  emptyTitle: string;
  emptyDescription?: string;
  footer?: ReactNode;
}

function AiCell({ user }: { user: AiUserRow }) {
  if (isMasterEmail(user.email)) {
    return <span className="text-xs text-muted-foreground">sem limite</span>;
  }
  return <CreditsBar className="w-28" percentUsed={user.credits.percentUsed} exhausted={user.credits.exhausted} />;
}

/** Tabela densa de usuários do painel. A linha abre o editor; o switch liga o acesso. */
export function UsersTable({
  rows,
  loading,
  fetching,
  showPassbook,
  storeCatalog = [],
  selection,
  onOpen,
  onViewAs,
  onToggleActive,
  busyIds,
  emptyTitle,
  emptyDescription,
  footer,
}: UsersTableProps) {
  const colCount = 7 + (selection ? 1 : 0) + (showPassbook ? 1 : 0) + (onToggleActive ? 1 : 0) + (onViewAs ? 1 : 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
      <div className="overflow-x-auto">
        <Table className={cn(fetching && !loading && 'opacity-70 transition-opacity')}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {selection ? (
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Selecionar todos da página"
                    checked={selection.allOnPageSelected ? true : selection.someOnPageSelected ? 'indeterminate' : false}
                    onCheckedChange={selection.toggleAllOnPage}
                  />
                </TableHead>
              ) : null}
              <TableHead>Usuário</TableHead>
              {showPassbook ? <TableHead className="hidden md:table-cell">Passbook</TableHead> : null}
              <TableHead>Perfil</TableHead>
              <TableHead className="hidden md:table-cell">Lojas</TableHead>
              <TableHead className="hidden text-right lg:table-cell">Páginas</TableHead>
              <TableHead>IA</TableHead>
              <TableHead className="hidden lg:table-cell">Último uso</TableHead>
              {onToggleActive ? <TableHead className="text-right">Ativo</TableHead> : null}
              {onViewAs ? <TableHead className="w-10" /> : null}
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={colCount}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount}>
                  <EmptyState icon={Users} title={emptyTitle} description={emptyDescription} className="py-10" />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((user) => {
                const stores = linkedStores(user.stores, user.storeCodes, storeCatalog);
                const customPages = Object.keys(user.pageFlags ?? {}).length;
                const master = isMasterEmail(user.email);
                return (
                  <TableRow
                    key={user.id}
                    className="cursor-pointer"
                    onClick={() => onOpen(user)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onOpen(user);
                    }}
                    tabIndex={0}
                  >
                    {selection ? (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          aria-label={`Selecionar ${user.name}`}
                          checked={selection.selectedIds.has(user.id)}
                          onCheckedChange={() => selection.toggle(user.id)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <div className="flex min-w-0 items-center gap-2.5">
                        <Avatar className="size-8">
                          <AvatarFallback className="text-xs">{initials(user.name || user.email)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{user.name || user.email}</p>
                          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    {showPassbook ? (
                      <TableCell className="hidden max-w-40 truncate text-sm md:table-cell">{user.passbook?.name ?? '—'}</TableCell>
                    ) : null}
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">
                        {panelRoleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden text-sm md:table-cell">
                      {stores.length === 0 ? (
                        <span className="text-muted-foreground">Todas</span>
                      ) : stores.length === 1 ? (
                        <span className="truncate">{stores[0].name}</span>
                      ) : (
                        <Tooltip content={stores.map((s) => s.name).join(', ')}>
                          <span>{stores.length} lojas</span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className="hidden text-right text-sm tabular-nums lg:table-cell">
                      {customPages > 0 ? (
                        <Badge variant="accent" className="font-normal">
                          {customPages} própria{customPages > 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">passbook</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <AiCell user={user} />
                    </TableCell>
                    <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">{formatDate(user.lastUsedAt, 'Nunca')}</TableCell>
                    {onToggleActive ? (
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Switch
                          aria-label={user.active ? 'Desativar acesso' : 'Ativar acesso'}
                          checked={user.active}
                          disabled={master || busyIds?.has(user.id)}
                          onCheckedChange={(next) => onToggleActive(user, next)}
                        />
                      </TableCell>
                    ) : null}
                    {onViewAs ? (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip content="Ver o painel como este usuário">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Ver como ${user.name || user.email}`}
                            disabled={!user.active || master}
                            onClick={() => onViewAs(user)}
                          >
                            <Eye className="size-4" />
                          </Button>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                    <TableCell className="text-muted-foreground">
                      <ChevronRight className="size-4" />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {footer ? <div className="border-t border-border/60 px-3 py-2">{footer}</div> : null}
    </div>
  );
}
